/**
 * The follow-up timeline and the activity feed — both built on `Widget/ActivityRow`
 * (node 6:408): p-8, `radius/sm`, a 28px round icon well in `status/info/bg` with a
 * `status/info/text` glyph, then `Body/13` `text/primary` over `Caption/12` `text/muted`.
 *
 * ===========================================================================
 * THE EXTRA CALL BADGE IS NOT IN FIGMA — AND IT IS NOT INVENTED EITHER
 * ===========================================================================
 * The brief requires an "Extra Call badge"; searching the file for one returns nothing,
 * and `Widget/InquiriesNeedingAction` (12:3101) — the only place follow-up work is drawn —
 * has no such chip. So it has to be authored. It is authored from a decision this codebase
 * has ALREADY made rather than a new one: `domain/events.ts` maps
 * `followup.created` with `kind === 'extra_call'` to `tone: 'warning'` and the `CircleDot`
 * glyph, and to `neutral`/`PencilLine` otherwise. Reusing that pair means the same fact
 * looks the same on the dashboard feed, in this timeline, and in the activity tab — which
 * is the whole point of an `extra_call` being a distinct enum value.
 *
 * `status/warning/*` is also the right MEANING: an extra call is a chased customer, not a
 * failure (danger) and not a completion (success). And the pair is already AA-corrected in
 * `tokens.css` (nudge ④), so nothing new is measured here.
 *
 * ===========================================================================
 * OVERDUE USES THE DESIGN'S OWN OVERDUE TREATMENT
 * ===========================================================================
 * A `next_action_at` in the past is drawn with `status/danger/*`, the same pair
 * `Pill/Days` uses for its overdue variant (node 12:3634 renders 12d that way). One
 * "you are late" colour across the app, taken from the component that already had one.
 */

import { CalendarClock, CircleDot, PencilLine } from 'lucide-react';
import type { FollowupDto } from '../../lib/inquiries-api';
import { eventToneClasses, presentEvent, relativeTime } from '../../domain/events';
import type { EventRow } from '../../domain/events';
import { cn } from '../../lib/cn';
import { formatDateLong, formatTime } from './format';
import { InquiryEmptyCard } from './states';

/**
 * "2 hours ago" · "Yesterday · 4:20 PM" · "18 Jul 2024 · 4:20 PM".
 *
 * Both strings in `Widget/ActivityRow`'s second line are reproduced: the design uses the
 * relative form for something that happened today (node 6:413, "2 hours ago") and the
 * dated form once it is older (I12:6502;6:413, "Yesterday · 4:20 PM"). The switch is at 24
 * hours, which is where `relativeTime` itself stops counting hours.
 */
export function formatWhen(iso: string | null, now: Date = new Date()): string {
  if (iso === null || iso === '') return '';
  const then = new Date(iso);
  if (!Number.isFinite(then.getTime())) return '';
  const hours = (now.getTime() - then.getTime()) / 3_600_000;
  if (hours < 24) return relativeTime(iso, now);
  const relative = relativeTime(iso, now);
  const time = formatTime(iso);
  if (relative === 'Yesterday') return time === '' ? relative : `${relative} · ${time}`;
  return time === '' ? formatDateLong(iso) : `${formatDateLong(iso)} · ${time}`;
}

/** Is a scheduled next action in the past? */
export function isNextActionOverdue(iso: string | null, now: Date = new Date()): boolean {
  if (iso === null || iso === '') return false;
  const when = new Date(iso).getTime();
  return Number.isFinite(when) && when < now.getTime();
}

/* ────────────────────────────────────────────────────── the row primitive ── */

export function ActivityRow({
  icon,
  toneClasses,
  text,
  meta,
  trailing,
}: {
  icon: ReactIcon;
  toneClasses: string;
  text: string;
  meta: string;
  trailing?: React.ReactNode;
}): React.JSX.Element {
  const Icon = icon;
  return (
    <div className="flex items-start gap-3 rounded-sm p-2">
      <span
        aria-hidden="true"
        className={cn(
          'flex size-7 shrink-0 items-center justify-center rounded-full',
          toneClasses,
        )}
      >
        <Icon className="size-3.5" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="whitespace-pre-wrap break-words text-body13 text-fg">{text}</p>
        <p className="text-label text-fg-muted">{meta}</p>
      </div>
      {trailing}
    </div>
  );
}

type ReactIcon = React.ComponentType<{ className?: string }>;

/* ──────────────────────────────────────────────── the extra-call badge ── */

export function ExtraCallBadge(): React.JSX.Element {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-pill bg-warn-bg px-2 py-0.5 text-label font-medium text-warn-fg">
      <CircleDot aria-hidden="true" className="size-3" />
      Extra call
    </span>
  );
}

/* ────────────────────────────────────────────────────── follow-up timeline ── */

export function FollowupTimeline({
  followups,
  now = new Date(),
  emptyMessage = 'No follow-ups yet.',
}: {
  followups: readonly FollowupDto[];
  now?: Date;
  emptyMessage?: string;
}): React.JSX.Element {
  if (followups.length === 0) {
    // L5's fourth spot — the quiet activity feed. The states.tsx author built the seam
    // and deliberately did not cross into this file ("wiring the `activity` spot there
    // is one word, whenever that file is in scope"); it then shipped as unreachable dead
    // code, which an adversarial pass caught by enumerating every render site. This is
    // the one word.
    return <InquiryEmptyCard className="max-w-none p-8" message={emptyMessage} spot="activity" />;
  }

  // Newest first. `GET /inquiries/:id` nests them, and the service does not promise an
  // order the UI can rely on, so the order is asserted here rather than assumed.
  const ordered = [...followups].sort((a, b) => {
    const left = a.created_at === null ? 0 : new Date(a.created_at).getTime();
    const right = b.created_at === null ? 0 : new Date(b.created_at).getTime();
    return right - left;
  });

  return (
    <ol className="flex flex-col gap-1">
      {ordered.map((followup) => {
        const extra = followup.kind === 'extra_call';
        const overdue = isNextActionOverdue(followup.next_action_at, now);
        return (
          <li key={followup.id} className="border-b border-line pb-1 last:border-b-0">
            <ActivityRow
              icon={extra ? CircleDot : PencilLine}
              toneClasses={eventToneClasses(extra ? 'warning' : 'neutral')}
              text={followup.note}
              meta={formatWhen(followup.created_at, now)}
              trailing={extra ? <ExtraCallBadge /> : undefined}
            />
            {followup.next_action_at === null ? null : (
              <p className="ml-12 pb-2">
                <span
                  className={cn(
                    'inline-flex items-center gap-1 rounded-pill px-2 py-0.5 text-label font-medium',
                    overdue
                      ? 'bg-danger-bg text-danger-fg'
                      : 'bg-subtle text-fg-secondary ring-1 ring-line',
                  )}
                >
                  <CalendarClock aria-hidden="true" className="size-3" />
                  {overdue ? 'Overdue since' : 'Next action'}{' '}
                  {formatDateLong(followup.next_action_at)}
                </span>
              </p>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/* ─────────────────────────────────────────────────────────── activity feed ── */

/**
 * The audit trail for one inquiry.
 *
 * Rendered through `presentEvent`, the foundation's verb→copy registry, so an
 * `inquiry.stage_changed` reads the same here as it does on the dashboard — and so an
 * UNDO shows up as its own row. That is the visible proof of Part A's undo semantics: two
 * events, both true, neither rewritten.
 */
export function InquiryActivityFeed({
  events,
  now = new Date(),
}: {
  events: readonly EventRow[];
  now?: Date;
}): React.JSX.Element {
  if (events.length === 0) {
    return (
      <InquiryEmptyCard
        className="max-w-none p-8"
        message="No activity recorded yet."
        spot="activity"
      />
    );
  }
  return (
    <ol className="flex flex-col gap-0.5">
      {events.map((event) => {
        const presentation = presentEvent(event);
        return (
          <li key={event.id}>
            <ActivityRow
              icon={presentation.Icon}
              toneClasses={eventToneClasses(presentation.tone)}
              text={presentation.text}
              meta={formatWhen(event.occurred_at, now)}
            />
          </li>
        );
      })}
    </ol>
  );
}
